import {
    Component,
    ComponentInterface,
    getAssetPath,
    h,
    Host,
    Prop,
    State,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";

type Background =
    | "background1"
    | "background2"
    | "background3"
    | "background4"
    | "background5"
    | "background6"
    | "background7"
    | "background8"
    | "background9"
    | "background-sf-2";

@Component({
    tag: "ux-iws-teaser-comparison",
    styleUrl: "teaser-comparison.scss",
    shadow: false,
    assetsDirs: ["backgrounds"],
})
export class TeaserComparison implements ComponentInterface {
    /**
     * The alignment of the slider
     */
    @Prop() sliderAlign: "left" | "right" = "right";

    /**
     * background image name (the names are the same as in the old PL component)
     */
    @Prop() background: Background | "" = "";

    /** this is used to calculate which background image should be used */
    @State() viewportWidth = 0;

    private resizeObserver: ResizeObserver;

    connectedCallback() {
        this.resizeObserver = new ResizeObserver(
            debounce(() => {
                this.viewportWidth = window.innerWidth;
            }, 100)
        );
        this.resizeObserver.observe(document.body);
        this.viewportWidth = window.innerWidth;
    }

    disconnectedCallback() {
        this.resizeObserver.disconnect();
    }

    private backgrounds: {
        [key in Background]: {
            image: string;
            mobileImage?: string;
            tabletImage?: string;
            desktopImage?: string;
            dark?: boolean;
        };
    } = {
        background1: {
            image: "background1.jpg",
        },
        background2: {
            image: "background2.jpg",
        },
        background3: {
            image: "background3.jpg",
            mobileImage: "background3-m.jpg",
            tabletImage: "background3-t.jpg",
            desktopImage: "background3-d.jpg",
            dark: true,
        },
        background4: {
            image: "background4.jpg",
            mobileImage: "background4-m.jpg",
            tabletImage: "background4-t.jpg",
            desktopImage: "background4-d.jpg",
            dark: true,
        },
        background5: {
            image: "background5.jpg",
            mobileImage: "background5-m.jpg",
            tabletImage: "background5-t.jpg",
            desktopImage: "background5-d.jpg",
        },
        background6: {
            image: "background6.jpg",
            mobileImage: "background6-m.jpg",
            tabletImage: "background6-t.jpg",
            desktopImage: "background6-d.jpg",
            dark: true,
        },
        background7: {
            image: "background7.jpg",
            mobileImage: "background7-m.jpg",
            tabletImage: "background7-t.jpg",
            desktopImage: "background7-d.jpg",
            dark: true,
        },
        background8: {
            image: "background8.jpg",
            mobileImage: "background8-m.jpg",
            tabletImage: "background8-t.jpg",
            desktopImage: "background8-d.jpg",
            dark: true,
        },
        background9: {
            image: "background9.jpg",
            mobileImage: "background9-m.jpg",
            tabletImage: "background9-t.jpg",
            desktopImage: "background9-d.jpg",
        },
        "background-sf-2": {
            image: "background-smart-factory-2.jpg",
        },
    };

    private getBackgroundImage() {
        const bg = this.backgrounds[this.background];
        if (!bg) {
            return "";
        }
        if (this.viewportWidth < 568 && bg.mobileImage) {
            return bg.mobileImage;
        }
        if (this.viewportWidth < 1025 && bg.tabletImage) {
            return bg.tabletImage;
        }
        if (
            this.viewportWidth >= 1025 &&
            this.viewportWidth < 1500 &&
            bg.desktopImage
        ) {
            return bg.desktopImage;
        }
        return bg.image;
    }

    render() {
        const alignLeft = this.sliderAlign === "left";
        const backgroundImage = this.background
            ? `url(${getAssetPath(`./backgrounds/${this.getBackgroundImage()}`)})`
            : undefined;
        return (
            <Host
                class="ux-iws-teaser-comparison__background"
                style={{ backgroundImage }}
                dark={this.backgrounds[this.background]?.dark}
            >
                <div
                    class={{
                        "ux-row": true,
                        "ux-u-flex-dir-row-reverse ux-u-flex-jc-end": alignLeft,
                    }}
                >
                    <div class="ux-col-10 ux-col-lg-4 ux-u-offset-1 ux-u-offset-xl-1">
                        <div class="ux-iws-teaser-comparison__text-block">
                            <slot name="text"></slot>
                        </div>
                    </div>
                    <div
                        class={{
                            "ux-col-11 ux-col-lg-5 ux-col-xl-4 ux-u-offset-1":
                                true,
                            "ux-u-offset-xl-2": alignLeft,
                        }}
                    >
                        <ux-slider class="ux-u-margin-top-20" effect="cards">
                            <slot></slot>
                        </ux-slider>
                    </div>
                </div>
            </Host>
        );
    }
}
